import {
  checkIsGoBackSIA,
  CONSUMER_CMS_SHOPPING_CART,
  ErrorCodes,
  getFwToken,
  getHubPath,
  goToHubProduct,
  IGenericErrorCMS,
  IProduct,
  isPersistData,
  resetKeyListAfterSubmit,
  resetKeyListOnRechargeMobile,
  updateProduct,
  useCmsConfig,
  useGetAddonsMobile,
} from '@vfit/consumer/data-access';
import { useQueryClient } from 'react-query';
import { useEffect, useState } from 'react';
import {
  resetData,
  errorManager,
  ErrorService,
  retrieveBasePageName,
  tracking,
} from '@vfit/shared/data-access';
import { API, getFromLocalStorageByKey, getHome } from '@vfit/shared/data-access';
import { ECodeIdentification, ICMSApiError, IDeliveryOptions } from '@vfit/shared/models';
import CheckoutModal from '../Checkout/checkout.modal';
import {
  updateProductPrice,
  updateProductShoppingCartUrl,
  retrieveAvailableAddons,
} from './shoppingCart.utils';
import { useCheckout } from '../iBuyMobile.context';
import { ENABLE_DEBUG } from '../iBuyMobile.utils';
import useShoppingCart from './shoppingCart.hook';
import { getTagging, getTrackError } from '../Checkout/checkout.utils';
import EnableDebug from '../Debug/enableDebug/enableDebug';

const ShoppingCart = () => {
  const { product, customerDeliveryBaseInfo, addonState, setProduct, setAddonState } =
    useCheckout();
  const queryString = window.location.search;
  const hub = product ? getHubPath(product) : '';
  const queryClient = useQueryClient();
  const [isLoadingCart, setIsLoadingCart] = useState<boolean>(true);
  const [isErrorOnPayment, setIsErrorOnPayment] = useState(false);
  const [isErrorOnPaymentShowErrorModal, setIsErrorOnPaymentShowErrorModal] = useState(false);
  const [isOnResetUser, setIsOnResetUser] = useState(false);
  const alreadySubmitterOrder = getFromLocalStorageByKey('submitOrder');
  const isNotFirstFlow = getFromLocalStorageByKey('shoppingCart');
  const recognitionMethod = localStorage.getItem('recognition') || '';
  const isEsim = localStorage.getItem('isEsim') === '1';
  const deliveryOptions: IDeliveryOptions[] =
    customerDeliveryBaseInfo?.deliveryBase?.deliveryOptions || [];
  const haveDI = deliveryOptions?.find((el) => el.code === ECodeIdentification.DI && el.isSelected);
  const isEsimFullyDigital = isEsim && recognitionMethod === ECodeIdentification.VR && haveDI;
  const {
    errorCart,
    isLoadingShoppingCartInfo,
    isErrorShoppingCart,
    errorCodeShoppingCart,
    isSuccessShoppingCart,
  } = useShoppingCart();

  const {
    data: getAddonsData,
    isSuccess: isSuccessGetAddons,
    isLoading: isLoadingGetAddons,
    refetch: refetchGetAddons,
  } = useGetAddonsMobile();

  const checkAvailableAddons = () => {
    refetchGetAddons();
  };

  const genericError = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_GENERIC_ERROR
  ) as IGenericErrorCMS;
  const cartErrors = useCmsConfig(
    CONSUMER_CMS_SHOPPING_CART,
    API.CMS_GET_CART_ERRORS_MOBILE
  ) as ICMSApiError;

  const checkErrorOnPayment = () => {
    if (
      queryString.includes(ErrorCodes.ERROR_ON_PAYMENT) ||
      isPersistData() ||
      queryString.includes(ErrorCodes.MODE_RESET_USER)
    ) {
      const isErrPayment = queryString.includes(ErrorCodes.ERROR_ON_PAYMENT);
      const isRstUser = queryString.includes(ErrorCodes.MODE_RESET_USER);
      setIsErrorOnPayment(isErrPayment);
      setIsErrorOnPaymentShowErrorModal(isErrPayment);
      setIsOnResetUser(isRstUser);
      if (isRstUser) {
        resetData(
          queryClient,
          resetKeyListOnRechargeMobile.filter((key) => key !== 'ctxk')
        );
      }
      setIsLoadingCart(false);
    } else if (alreadySubmitterOrder) {
      resetData(queryClient, [...resetKeyListAfterSubmit, ...resetKeyListOnRechargeMobile]);
    } else if (isNotFirstFlow) {
      resetData(queryClient, resetKeyListOnRechargeMobile);
    }
  };

  const trackErrorCart = () => {
    const errorObj = errorCart ? getTrackError(errorCart, 'shopping cart', queryClient) : {};
    const taggingProduct = getTagging('', 'mobile', queryClient);
    const pageName = `${retrieveBasePageName(
      taggingProduct?.trackingProduct,
      taggingProduct?.trackingProduct?.cart_product_name
    )}`;
    tracking(
      {
        ...errorObj,
        event_name: ['page_error'],
        event_category: 'error',
        page_name: `${pageName}:page_error`,
        page_type: 'error_page',
        product_name: taggingProduct?.pageProductsInfo.product_name,
        ...(errorCodeShoppingCart && { page_error_code: errorCodeShoppingCart }),
      },
      'view'
    );
  };

  useEffect(() => {
    if (isErrorShoppingCart) {
      const closeModalAction = (url?: string) => {
        resetData(queryClient, ['shoppingCart']);
        if (url) window.location.href = url;
        else {
          const hubPath = product?.hubPath || '';
          window.location.href = `${getHome()}/${hubPath}`;
        }
      };
      trackErrorCart();
      const foundedCartError = cartErrors?.errorcomponent?.errors?.find(
        (e) => e.errorCode === errorCodeShoppingCart
      );
      errorManager.handleError(ErrorService.getSeverityErrorHigh(), {
        title:
          foundedCartError?.title ||
          cartErrors?.errorcomponent?.defaultTitle ||
          genericError?.genericerror?.title ||
          '',
        message:
          foundedCartError?.message ||
          cartErrors?.errorcomponent?.defaultMessage ||
          genericError?.genericerror?.description ||
          '',
        disableTrack: true,
        actionText:
          foundedCartError?.button?.title || genericError?.genericerror?.buttonLabel || '',
        onClose: closeModalAction,
        actionEvent: () =>
          closeModalAction(
            foundedCartError?.button?.title ? foundedCartError?.button?.urlForRedirect : ''
          ),
        ...(errorCart && { trackError: getTrackError(errorCart, 'shopping cart', queryClient) }),
        opts: {
          product_name: product?.slug || '',
        },
      });
    }
  }, [isErrorShoppingCart]);

  useEffect(() => {
    if (product?.isLoaded && !isLoadingShoppingCartInfo) {
      setIsLoadingCart(false);
    }
  }, [product]);

  useEffect(() => {
    if (!isLoadingShoppingCartInfo) {
      const prod = product as IProduct;
      setProduct({
        ...prod,
        ...(prod.isWinback ? {} : updateProductPrice(prod)),
        shoppingCartUrl: window.location.href,
        isLoaded: true,
      });
      setTimeout(() => {
        updateProductShoppingCartUrl(product);
      }, 1000);
    }
  }, [isLoadingShoppingCartInfo]);

  useEffect(() => {
    if (isLoadingGetAddons) {
      setAddonState({
        ...addonState,
        isLoading: true,
        callGetAddons: true,
      });
    }
    if (isSuccessGetAddons && getAddonsData) {
      const _availableAddons = retrieveAvailableAddons(getAddonsData);
      localStorage.setItem('available_addons', JSON.stringify(_availableAddons));
      setAddonState({
        ...addonState,
        callGetAddons: false,
        availableAddons: _availableAddons,
        isLoading: false,
        isSuccess: true,
      });
    }
  }, [isLoadingGetAddons, isSuccessGetAddons, getAddonsData]);

  useEffect(() => {
    if (isSuccessShoppingCart && product?.addonList && product?.addonList.length > 0) {
      checkAvailableAddons();
    }
  }, [isSuccessShoppingCart]);

  useEffect(() => {
    const prod = product as IProduct;
    setProduct({
      ...prod,
      ...updateProductPrice(prod),
    });
  }, [isEsimFullyDigital]);

  useEffect(() => {
    checkErrorOnPayment();
    if (product) {
      updateProduct({
        ...product,
        ...(hub && { hubPath: hub }),
      });
    }
  }, []);

  if (!product || Object.keys(product)?.length === 0) return null;

  if (product?.isTpoOffer && !product?.forceCampaign && !getFwToken()) {
    errorManager.handleError(ErrorService.getSeverityErrorHigh(), {
      title: 'Ops',
      onClose: () => {
        window.location.href = '/';
      },
      message: 'Si è verificato un problema. Riprovare in seguito',
      disableCloseModalOnClickAction: true,
    });
    return null;
  }

  return (
    <>
      {ENABLE_DEBUG && <EnableDebug />}
      <CheckoutModal
        isOpen
        handleClose={goToHubProduct}
        backgroundImage={product?.image || ''}
        isLoadingCart={isLoadingCart}
        isErrorOnPayment={isErrorOnPayment}
        isErrorOnPaymentShowErrorModal={isErrorOnPaymentShowErrorModal}
        isOnResetUser={isOnResetUser}
        hideCards={checkIsGoBackSIA()}
      />
    </>
  );
};

export default ShoppingCart;

